import { axiosClient } from "./axiosClient";
import { IBaseRespone } from "../model/respone/IBaseRespone";
import { appConst } from "../AppConst";
export const objectToQueryString = (obj: any, parentKey: any): string => {
  return Object.keys(obj)
    .map((key) => {
      const value = obj[key];
      const encodedKey = parentKey
        ? `${parentKey}.${encodeURIComponent(key)}`
        : encodeURIComponent(key);

      if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        value !== null
      ) {
        return objectToQueryString(value, encodedKey);
      }

      return `${encodedKey}=${encodeURIComponent(value)}`;
    })
    .join("&");
};
export const formatQueryString = (obj: any): string => {
  if (obj) {
    return objectToQueryString(obj, null);
  }
  return "";
};
// Token giờ nằm trong cookie httpOnly "sid" do backend đặt — FE KHÔNG đọc/lưu token nữa.
// Giữ các hàm này (dọn key localStorage cũ nếu còn) để nơi đang import không vỡ.
export const clearAccessToken = () => {
  localStorage.removeItem("access_token");
};
export const clearRefreshToken = () => {
  localStorage.removeItem("refresh_token");
};
export const saveAccessToken = (_access_token?: string) => {};
export const saveRefreshToken = (_refresh_token?: string) => {};
export const getAccessToken = () => localStorage.getItem("access_token");

const language = () => localStorage.getItem("language");

const UNAUTHORIZED: IBaseRespone = {
  status_code: 401,
  is_success: false,
  message:
    "Bạn không được phân quyền hoặc hết phiên đăng nhập để thực hiện thao tác này. Vui lòng đăng nhập lại hoặc liên hệ Quản trị viên để được hỗ trợ.",
};

// Callback báo HẾT PHIÊN (refresh thất bại) — App đăng ký để clear user, đưa về màn đăng nhập.
let onSessionExpired: (() => void) | null = null;
export const setOnSessionExpired = (fn: (() => void) | null) => {
  onSessionExpired = fn;
};

// Single-flight refresh: khi API trả 401, gọi /account/token/refresh (refresh token lấy từ cookie phiên),
// rồi thử lại request gốc ĐÚNG 1 lần. Thất bại → trả lỗi 401 (FE điều hướng đăng nhập).
let refreshPromise: Promise<boolean> | null = null;
const doRefresh = (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = axiosClient
      .post<any, IBaseRespone>(`${appConst.baseApiURL}/account/token/refresh`, {})
      .then((res: any) => !!res?.is_success)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Chạy request; nếu 401 thì refresh 1 lần rồi gọi lại. Chuẩn hóa lỗi về IBaseRespone.
const run = async (fn: () => Promise<IBaseRespone>): Promise<IBaseRespone> => {
  try {
    return await fn();
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const ok = await doRefresh();
      if (ok) {
        try {
          return await fn(); // refresh xong, thử lại thành công
        } catch {
          // thử lại vẫn lỗi → coi như hết phiên
        }
      }
      // Hết phiên thật (refresh thất bại / retry vẫn 401) → đưa về màn đăng nhập.
      onSessionExpired?.();
      return UNAUTHORIZED;
    }
    return {
      status_code: error?.response?.data?.status_code,
      is_success: false,
      message: error?.response?.data?.message || "Có lỗi",
    };
  }
};

// Ghép baseApiURL vào path. Một số nơi đã tự prepend sẵn appConst.baseApiURL vào path (trước đây baseApiURL
// là URL tuyệt đối nên check "http" tránh lặp). Khi baseApiURL là đường dẫn tương đối ("/api") thì phải
// tự dò để KHÔNG ghép lặp → tránh "/api//api/..." (không khớp route, rơi vào SPA fallback).
const buildUrl = (path: string, domain?: string): string => {
  if (path.includes("http")) return path;
  const base = domain ? domain : appConst.baseApiURL;
  if (base && (path === base || path.startsWith(`${base}/`))) return path;
  return `${base}/${path}`;
};

const apiClient = {
  get: (path: string): Promise<IBaseRespone> => {
    const url = buildUrl(path);
    return run(() =>
      axiosClient.get<any, IBaseRespone>(url, { headers: { language: language() } }),
    );
  },
  post: (path: string, data?: any, domain?: string): Promise<IBaseRespone> => {
    const url = buildUrl(path, domain);
    return run(() =>
      axiosClient.post<any, IBaseRespone>(url, data, {
        headers: { language: language() },
      }),
    );
  },
  put: (path: string, data?: any): Promise<IBaseRespone> => {
    const url = buildUrl(path);
    return run(() =>
      axiosClient.put<any, IBaseRespone>(url, data, {
        headers: { language: language() },
      }),
    );
  },
  delete: (path: string): Promise<IBaseRespone> => {
    const url = buildUrl(path);
    return run(() =>
      axiosClient.delete<any, IBaseRespone>(url, { headers: { language: language() } }),
    );
  },
  upload: (path: string, data?: any): Promise<IBaseRespone> => {
    const url = buildUrl(path);
    return run(() =>
      axiosClient.post<any, IBaseRespone>(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          language: language(),
        },
      }),
    );
  },
};
export { apiClient };
